// @flow
import { times, get } from 'lodash';
import moment from 'moment';

import {
  DEFAULT_KID_VALUE,
  VALIDATION_STATE_ENUM,
  type ValidationState,
} from 'Store/trip-information/kids/constants';
import { type Kids } from 'Store/trip-information/kids/kids.reducer';
import { QUERY_DATE_FORMAT, QUERY_KIDS_DELIMITER, QUERY_CHILDCARE_FLAG } from 'Utils/uri/constants';
import {
  CHILDCARE_FACET_ID,
  DEPARTURE_CITY_FACET_ID,
  DATE_FORMAT,
  KIDS_NUMBER_MAX,
} from './constants';

function findFacetCategoryById(
  facets: Array<SearchFacetCategory>,
  facetCategoryId: string,
): ?SearchFacetCategory {
  return facets.find(facetCategory => get(facetCategory, 'id') === facetCategoryId);
}

function _getFacetValues(
  facets: Array<SearchFacetCategory>,
  categoryId: string,
): Array<FacetValue> {
  const category: ?SearchFacetCategory = findFacetCategoryById(facets, categoryId);
  return get(category, 'list', []);
}

function getChildcareFacets(facets: Array<SearchFacetCategory>): Array<FacetValue> {
  return _getFacetValues(facets, CHILDCARE_FACET_ID);
}

function getDepartureCities(facets: Array<SearchFacetCategory>): Array<FacetValue> {
  return _getFacetValues(facets, DEPARTURE_CITY_FACET_ID);
}

function getSelectedDepartureCity(facets: Array<SearchFacetCategory>): ?string {
  const selectedCity: ?FacetValue = getDepartureCities(facets).find(facet =>
    get(facet, 'selected'),
  );

  return selectedCity ? get(selectedCity, 'name') : null;
}

type Date = number | string | moment$Moment;
function formatDepartureDateForStore(date: ?Date, format: string = DATE_FORMAT): ?timestamp {
  return date !== null ? moment(date, format).valueOf() : null;
}

function getDepartureDateDisplayValue(date: ?timestamp = null): string {
  const momentDate = moment(date);

  return momentDate.isValid() ? momentDate.format(DATE_FORMAT) : '';
}

function isDateInThePast(timestampDate?: timestamp): boolean {
  return timestampDate ? moment(timestampDate).isBefore() : false;
}

function getFormattedDepartureDate(date?: timestamp, dateFormat: string): string {
  return date ? moment(date).format(dateFormat) : '';
}

function isKidMinor(birthdate: timestamp): boolean {
  if (!birthdate) {
    return false;
  }
  const minBirthdate: moment = moment().subtract(18, 'years');
  const momentBirthdate: moment = moment(birthdate);
  return momentBirthdate.isValid() && momentBirthdate.isBetween(minBirthdate, moment());
}

function getChildren(numberOfChildren: number, childrenBirthdates: Array<string>): * {
  if (
    Number.isInteger(numberOfChildren) &&
    numberOfChildren >= 0 &&
    numberOfChildren <= KIDS_NUMBER_MAX
  ) {
    return times(
      numberOfChildren,
      idx =>
        childrenBirthdates && childrenBirthdates.length > idx
          ? {
              birthdate: moment(childrenBirthdates[idx], 'YYYYMMDD', true).valueOf(),
              childcareIncluded: false,
            }
          : { childcareIncluded: false },
    );
  }
  return null;
}

function unserializeKidsFromQueryParam(value: string): Array<Kids> {
  if (!value) return [];
  const kidsNumber = Number(value); // NOTE: DO NOT USE parseInt() !!! parseInt('2010-12-10c') will return 2010 ...

  if (kidsNumber > KIDS_NUMBER_MAX) {
    return [];
  }
  if (Number.isInteger(kidsNumber)) return new Array(kidsNumber).fill({ ...DEFAULT_KID_VALUE });

  const kids = value.split(QUERY_KIDS_DELIMITER).map<Kids>((serializedKidString: string) => {
    const hasChildcareFlag = serializedKidString.substr(-1) === QUERY_CHILDCARE_FLAG;
    const strBirthdate = serializedKidString.replace(QUERY_CHILDCARE_FLAG, '');
    const birthdate = moment(strBirthdate, QUERY_DATE_FORMAT, true);
    const childcareIncluded = birthdate.isValid()
      ? hasChildcareFlag
      : DEFAULT_KID_VALUE.childcareIncluded;

    return {
      ...DEFAULT_KID_VALUE,
      childcareIncluded,
      birthdate: birthdate.isValid() ? birthdate.valueOf() : undefined,
    };
  });

  return kids.length > KIDS_NUMBER_MAX ? [] : kids;
}

/**
 * Helper to serialize kids array as a URI query parameter.
 *
 * Example of returns:
 *
 * - `kids=1` (1 kid, no birthdate)
 * - `kids=2010-10-30` (1 kid, with birthdate)
 * - `kids=_` (2 kids)
 * - `kids=2010-10-30=_` (2 kids, 1 birthdate)
 * - `kids=2010-10-30c=___`
 * - `kids=2010-10-30c_2009-04-01!`
 */
export const serializeKidsToQueryParam = (params: {
  kids: Array<Kids>,
  kidsValidationState: Array<ValidationState>,
}) => {
  const { kids, kidsValidationState } = params;
  const serializedKids = kids.map(({ birthdate, childcareIncluded }, index) => {
    if (!kidsValidationState || kidsValidationState[index] !== VALIDATION_STATE_ENUM.valid) {
      return '';
    }

    const formattedBirthdate = moment(birthdate).format(QUERY_DATE_FORMAT);

    if (childcareIncluded) {
      return `${formattedBirthdate}${QUERY_CHILDCARE_FLAG}`;
    }

    return formattedBirthdate;
  });

  // Special case when there is only one kid, as we can't make a join('_') with only one item
  if (serializedKids.length === 1) {
    if (serializedKids.filter(Boolean).length === 0) {
      return '1';
    }
    // Return kid's birthdate
    return serializedKids.shift();
  }

  return serializedKids.join(QUERY_KIDS_DELIMITER);
};

export {
  getDepartureDateDisplayValue,
  findFacetCategoryById,
  formatDepartureDateForStore,
  getChildcareFacets,
  getChildren,
  getFormattedDepartureDate,
  getDepartureCities,
  getSelectedDepartureCity,
  isDateInThePast,
  isKidMinor,
  unserializeKidsFromQueryParam,
};
